import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { io } from "socket.io-client";
import {
  MdVideoCall,
  MdAttachFile,
  MdImage,
  MdInsertDriveFile,
  MdClose,
  MdSend,
} from "react-icons/md";
import { useNavigate } from "react-router-dom";

const socket = io("https://api.confidanto.com");

const ChatBox = ({ isOpen, toggleChat }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [showAttachmentOptions, setShowAttachmentOptions] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showInput, setShowInput] = useState(false);
  const [userData, setUserData] = useState(null);
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isOpen) {
      const fetchInfo = async () => {
        try {
          const res = await axios.post(
            "https://api.confidanto.com/header-data",
            {
              email: localStorage.getItem("email"),
            }
          );
          const fetchedUserData = res.data.userData;
          setUserData(fetchedUserData);

          const historyRes = await axios.get(
            `https://api.confidanto.com/chatbox-api/api/messages/${fetchedUserData.email}`
          );

          if (historyRes.data && historyRes.data.length > 0) {

            setMessages(historyRes.data);
          } else {
            const welcomeMessage = {
              from: "manager",
              text: "Hello! I'm John, your account manager. You can reach me at john@example.com or call me at +1234567890. How can I assist you today?",
              timestamp: new Date(),
            };
            const notificationMessage = {
              from: "system",
              text: "How can I assist you today?",
              timestamp: new Date(),
            };
            setMessages([welcomeMessage, notificationMessage]);
          }
          setShowInput(true);
        } catch (error) {
          console.error("Error fetching user data or chat history:", error);
        }
      };

      fetchInfo();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleNewMessage = (newMessage) => {
      setMessages((prevMessages) => [...prevMessages, newMessage]);
    };

    socket.on("newMessage", handleNewMessage);

    if (userData?.email) {
      socket.on(`adminMessage:${userData.email}`, handleNewMessage);
    }

    return () => {
      socket.off("newMessage", handleNewMessage);
      if (userData?.email) {
        socket.off(`adminMessage:${userData.email}`, handleNewMessage);
      }
    };
  }, [userData]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (inputValue.trim() && userData?.email) {
      const newMessage = {
        from: "user",
        text: inputValue,
        timestamp: new Date(),
      };

      setMessages((prevMessages) => [...prevMessages, newMessage]);

      try {
        await axios.post(
          "https://api.confidanto.com/chatbox-api/api/messages",
          {
            email: userData.email,
            text: inputValue,
          }
        );

        socket.emit("sendMessage", {
          email: userData.email,
          text: inputValue,
        });
      } catch (error) {
        console.error("Error saving message to database:", error);
      }

      setInputValue("");
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSendMessage();
    }
  };

  const toggleAttachmentOptions = () => {
    setShowAttachmentOptions(!showAttachmentOptions);
  };

  const handleVideoCallClick = () => {
    toggleAttachmentOptions();
    navigate("/ScheduleCall");
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);

      const newMessage = {
        from: "user",
        text: `Sent an attachment: ${file.name}`,
        fileUrl: URL.createObjectURL(file),
        timestamp: new Date(),
      };
      setMessages((prevMessages) => [...prevMessages, newMessage]);
    }
    setShowAttachmentOptions(false);
  };

  return isOpen ? (
    <div className="fixed bottom-20 right-4 w-full sm:w-96 bg-white rounded-2xl shadow-2xl flex flex-col max-h-[calc(100vh-15rem)] transition-all duration-300 ease-in-out">
      {/* Chat Header */}
      <header className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 text-white flex items-center justify-between rounded-t-2xl shadow-md">
        <div>
          <h3 className="text-lg font-bold">Chat with us</h3>
          <p className="text-xs opacity-90">We'll reply as soon as we can</p>
        </div>
        <button
          onClick={toggleChat}
          className="text-white hover:bg-white/20 rounded-full p-1.5 focus:outline-none focus:ring-2 focus:ring-white/50 transition-colors"
        >
          <MdClose className="w-5 h-5" />
        </button>
      </header>

      {/* Messages Area */}
      <main className="flex-1 p-4 overflow-y-auto space-y-4 bg-gray-50">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex items-end gap-2 ${
              message.from === "admin" || message.from === "manager"
                ? "justify-start"
                : "justify-end"
            }`}
          >
            <div
              className={`inline-block py-2 px-3 rounded-xl max-w-xs text-sm shadow-sm ${
                message.from === "admin" || message.from === "manager"
                  ? "bg-white text-gray-800 rounded-bl-none"
                  : "bg-blue-500 text-white rounded-br-none"
              }`}
            >
              <p className="break-words">{message.text}</p>
              {message.fileUrl && (
                <a
                  href={message.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block mt-2"
                >
                  {message.fileUrl.match(/\.(jpeg|jpg|gif|png)$/) ? (
                    <img
                      src={message.fileUrl}
                      alt="attachment"
                      className="max-w-full h-auto rounded-lg mt-1"
                    />
                  ) : (
                    <span className="text-blue-200 underline hover:text-white transition-colors">
                      {message.text}
                    </span>
                  )}
                </a>
              )}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </main>

      {/* Input Area */}
      {showInput && (
        <footer className="p-3 border-t border-gray-200 bg-white rounded-b-2xl">
          <div className="flex items-center space-x-2 relative">
            <div className="relative">
              <button
                className="p-2 text-gray-500 hover:text-blue-600 rounded-full hover:bg-gray-100 focus:outline-none transition-colors"
                onClick={toggleAttachmentOptions}
              >
                <MdAttachFile className="w-6 h-6" />
              </button>
              {showAttachmentOptions && (
                <div className="absolute bottom-full mb-2 left-0 bg-white shadow-lg rounded-lg p-2 flex space-x-2 border border-gray-200">
                  <button
                    className="p-2 text-gray-600 hover:text-blue-600 focus:outline-none hover:bg-gray-100 rounded-full"
                    onClick={handleVideoCallClick}
                  >
                    <MdVideoCall className="w-6 h-6" title="Schedule Video Call" />
                  </button>
                  <label className="p-2 text-gray-600 hover:text-blue-600 focus:outline-none hover:bg-gray-100 rounded-full cursor-pointer">
                    <MdImage className="w-6 h-6" title="Attach Image" />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </label>
                  <label className="p-2 text-gray-600 hover:text-blue-600 focus:outline-none hover:bg-gray-100 rounded-full cursor-pointer">
                    <MdInsertDriveFile
                      className="w-6 h-6"
                      title="Attach File"
                    />
                    <input
                      type="file"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </label>
                </div>
              )}
            </div>
            <input
              type="text"
              className="flex-grow py-2 px-4 bg-gray-100 border border-transparent rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
              placeholder="Type a message..."
              value={inputValue}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
            />
            <button
              className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600 disabled:opacity-50 transition-all duration-200"
              onClick={handleSendMessage}
              disabled={!inputValue.trim()}
            >
              <MdSend className="w-5 h-5" />
            </button>
          </div>
        </footer>
      )}
    </div>
  ) : null;
};

export default ChatBox;